import React from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { PAGE_SIZE_OPTIONS } from '@/utils/urlListState';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Merged last onto the wrapper, per the repo component conventions. */
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className = '',
}) => {
  // The plan compacts the pager "below sm" (640px) — one band earlier than the
  // rest of the phone templates, which switch below `md`.
  const { isPhone } = useBreakpoint();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems === 0) {
    return null;
  }

  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  if (isPhone) {
    const compactButton =
      'flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-700 active:bg-gray-100 disabled:opacity-50 disabled:active:bg-transparent';

    return (
      <div
        data-tap-region
        className={`mt-4 flex items-center justify-between gap-3 ${className}`.trim()}
      >
        <p className="min-w-0 truncate text-[13px] text-gray-600">
          Menampilkan {from}-{to} dari {totalItems}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Halaman sebelumnya"
            className={compactButton}
            disabled={currentPage <= 1}
            onClick={() => {
              if (currentPage > 1) onPageChange(currentPage - 1);
            }}
          >
            <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
          </button>
          <span className="whitespace-nowrap text-[13px] text-gray-900 tabular-nums">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            aria-label="Halaman berikutnya"
            className={compactButton}
            disabled={currentPage >= totalPages}
            onClick={() => {
              if (currentPage < totalPages) onPageChange(currentPage + 1);
            }}
          >
            <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mt-4 flex items-center justify-between ${className}`.trim()}
    >
      {/* Total Items - Left */}
      <div className="text-[12px] text-gray-700">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{' '}
        results
      </div>

      {/* Page Navigation - Center */}
      <div className="flex items-center space-x-1">
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-white hover:text-black disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage > 1) {
              onPageChange(1);
            }
          }}
        >
          <i className="ri-arrow-left-double-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-white hover:text-black disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage > 1) {
              onPageChange(currentPage - 1);
            }
          }}
        >
          <i className="ri-arrow-left-s-line text-[10px]"></i>
        </button>

        {(() => {
          const maxVisiblePages = 5;
          let startPage = Math.max(
            1,
            currentPage - Math.floor(maxVisiblePages / 2)
          );
          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

          if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
          }

          const pages = [];
          for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
          }

          return pages.map((page) => (
            <button
              key={page}
              className={`px-3 py-1 text-[12px] border rounded-md hover:bg-gray-50 ${
                currentPage === page
                  ? 'border-primary bg-primary text-white hover:text-black'
                  : 'border-gray-300 text-black'
              }`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ));
        })()}

        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => {
            if (currentPage < totalPages) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => {
            if (currentPage < totalPages) {
              onPageChange(totalPages);
            }
          }}
        >
          <i className="ri-arrow-right-double-line text-[10px]"></i>
        </button>
      </div>

      {/* Page Size Selector - Right */}
      <div className="flex items-center space-x-2">
        {onPageSizeChange && (
          <>
            <span className="text-[12px] text-gray-700">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-base md:text-[12px] border border-gray-300 rounded-md px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:border-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-[12px] text-gray-700">/ page</span>
          </>
        )}
      </div>
    </div>
  );
};

export default Pagination;
